/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import { Cell, FormControl, Grid, VFlow } from 'bold-ui'
import { FormAccordionButton } from 'components/accordion/FormSectionAccordionButton'
import { RadioField, RadioFieldProps } from 'components/form'
import { RadioGroupField } from 'components/form/field/RadioGroupField'
import theme from 'config/theme'
import { CaracteristicaLesaoEnum } from 'graphql/types.generated'
import { Accordion, AccordionItem, AccordionItemPanel } from 'react-accessible-accordion'
import { MetaPath } from 'util/metaPath'

import { getTecidosMolesCaracteristicaLesao } from '../../model'
import LesaoFundamentalField, { LesaoFundamentalFieldModel } from './components/LesaoFundamentalField'
import SintamotologiaField, { SintamotologiaFieldModel } from './components/SintamotologiaField'
import SuperficieTecidosMolesField, { SuperficieTecidosMolesFieldModel } from './components/SuperficieTecidosMolesField'

export interface CaracteristicasLesaoFieldModel {
  lesaoFundamental: LesaoFundamentalFieldModel
  quantidade: CaracteristicaLesaoEnum
  base: CaracteristicaLesaoEnum
  tamanho: CaracteristicaLesaoEnum
  cor: CaracteristicaLesaoEnum
  superficie: SuperficieTecidosMolesFieldModel
  consistencia: CaracteristicaLesaoEnum
  sintamotologia: SintamotologiaFieldModel
}

export interface CaracteristicasLesaoFieldProps {
  name: MetaPath<CaracteristicasLesaoFieldModel>
  preExpanded?: boolean
}

export default function CaracteristicasLesaoField(props: CaracteristicasLesaoFieldProps) {
  const { name, preExpanded } = props

  return (
    <Accordion allowZeroExpanded={true} preExpanded={preExpanded ? ['lesao-tecidos-moles'] : []}>
      <AccordionItem uuid='lesao-tecidos-moles'>
        <FormAccordionButton text='Características da lesão' />
        <AccordionItemPanel css={classes.accordionItemPanel}>
          <Grid gap={1}>
            <LesaoFundamentalField name={name.lesaoFundamental} />

            <Cell size={6}>
              <FormControl label='Quantidade'>
                <VFlow vSpacing={0.5}>
                  <RadioGroupField
                    name={name.quantidade}
                    lines={3}
                    vSpacing={0.5}
                    clearable
                    options={convertCaracteristicasLesaoToRadioOptions([
                      CaracteristicaLesaoEnum.QUANTIDADE_UNICA,
                      CaracteristicaLesaoEnum.QUANTIDADE_BILATERAL,
                      CaracteristicaLesaoEnum.QUANTIDADE_MULTIPLAS,
                    ])}
                  />
                </VFlow>
              </FormControl>
            </Cell>

            <Cell size={6}>
              <FormControl label='Implantação ou base'>
                <Grid gapVertical={0.5} gap={1}>
                  <Cell size={5}>
                    <RadioFieldStyled name={name.base} value={CaracteristicaLesaoEnum.BASE_SESSIL} />
                  </Cell>
                  <Cell size={7}>
                    <RadioFieldStyled name={name.base} value={CaracteristicaLesaoEnum.BASE_PEDICULADA} />
                  </Cell>
                </Grid>
              </FormControl>
            </Cell>

            <Cell size={6}>
              <FormControl label='Tamanho'>
                <VFlow vSpacing={0.5}>
                  <RadioGroupField
                    name={name.tamanho}
                    lines={3}
                    vSpacing={0.5}
                    clearable
                    options={convertCaracteristicasLesaoToRadioOptions([
                      CaracteristicaLesaoEnum.TAMANHO_ATE_4,
                      CaracteristicaLesaoEnum.TAMANHO_ENTRE_5_E_10,
                      CaracteristicaLesaoEnum.TAMANHO_ACIMA_DE_10,
                    ])}
                  />
                </VFlow>
              </FormControl>
            </Cell>

            <Cell size={6}>
              <FormControl label='Cor'>
                <Grid gapVertical={0.5} gap={1}>
                  <Cell size={5}>
                    <RadioFieldStyled name={name.cor} value={CaracteristicaLesaoEnum.COR_BRANCA} />
                  </Cell>
                  <Cell size={7}>
                    <RadioFieldStyled name={name.cor} value={CaracteristicaLesaoEnum.COR_VERMELHA} />
                  </Cell>
                  <Cell size={5}>
                    <RadioFieldStyled name={name.cor} value={CaracteristicaLesaoEnum.COR_VIOLACEA} />
                  </Cell>
                  <Cell size={7}>
                    <RadioFieldStyled name={name.cor} value={CaracteristicaLesaoEnum.COR_ACASTANHADA} />
                  </Cell>
                </Grid>
              </FormControl>
            </Cell>

            <Cell size={6}>
              <SuperficieTecidosMolesField name={name.superficie} />
            </Cell>

            <Cell size={6}>
              <FormControl label='Consistência'>
                <Grid gapVertical={0.5}>
                  <Cell size={5}>
                    <RadioFieldStyled name={name.consistencia} value={CaracteristicaLesaoEnum.CONSISTENCIA_MOLE} />
                  </Cell>
                  <Cell size={7}>
                    <RadioFieldStyled name={name.consistencia} value={CaracteristicaLesaoEnum.CONSISTENCIA_FIRME} />
                  </Cell>
                  <Cell size={5}>
                    <RadioFieldStyled name={name.consistencia} value={CaracteristicaLesaoEnum.CONSISTENCIA_DURA} />
                  </Cell>
                </Grid>
              </FormControl>
            </Cell>

            <Cell size={12}>
              <SintamotologiaField name={name.sintamotologia} />
            </Cell>
          </Grid>
        </AccordionItemPanel>
      </AccordionItem>
    </Accordion>
  )
}

const RadioFieldStyled = (props: { name: RadioFieldProps['name']; value: CaracteristicaLesaoEnum }) => (
  <RadioField
    label={getTecidosMolesCaracteristicaLesao(props.value).title}
    style={{ marginLeft: -0.25 }}
    wrapperStyles={classes.wrapperStyles}
    clearable
    {...props}
  />
)

const convertCaracteristicasLesaoToRadioOptions = (caracteristicas: CaracteristicaLesaoEnum[]) =>
  caracteristicas.map((caracteristica) => ({
    label: getTecidosMolesCaracteristicaLesao(caracteristica).title,
    value: caracteristica,
  }))

const classes = {
  accordionItemPanel: css`
    padding: 1em;
    border: 1px ${theme.pallete.divider} solid;
    border-radius: 2px;
    border-top: 0;
    margin-top: -1px;
  `,
  wrapperStyles: css`
    ${theme.breakpoints.down('xl')} {
      grid-gap: 0.5rem;
    }
  `,
}
